import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/* -------------------------------------------------------------------------- */
/* Tenant date / time display format                                          */
/*                                                                            */
/* Each tenant picks a date format and a time format at onboarding. The API   */
/* formats every date it returns with them, so a screen shows the text as it  */
/* arrives. This module is for the rest:                                      */
/*  - values that still arrive as ISO-8601 (e.g. parameter tables), which are */
/*    formatted here with the same patterns;                                  */
/*  - date pickers and edit forms, which need the pattern to show and parse,  */
/*    and must send ISO-8601 (yyyy-MM-dd) back to the API.                    */
/*                                                                            */
/* Never parse an API date with `new Date(text)` / `dayjs(text)`: the browser */
/* reads "05/06/2026" as 6 May. Use parseApiDate, which only accepts ISO or   */
/* the tenant's own pattern.                                                  */
/* -------------------------------------------------------------------------- */

/** As returned by GET /AwardManagement/tenant-settings/date-format (.NET patterns). */
export interface TenantDateFormat {
  datePattern: string;
  timePattern: string;
  dateTimePattern: string;
  timeZone: string;
}

/** Singapore default — what the API uses when onboarding chose nothing. */
export const DEFAULT_TENANT_DATE_FORMAT: TenantDateFormat = {
  datePattern: 'dd/MM/yyyy',
  timePattern: 'hh:mm tt',
  dateTimePattern: 'dd/MM/yyyy hh:mm tt',
  timeZone: 'Asia/Singapore',
};

const STORAGE_KEY = 'sv_tenant_date_format';

interface ResolvedFormat {
  /** dayjs patterns. */
  date: string;
  dateTime: string;
  timeZone: string;
}

/**
 * Translates a .NET custom date pattern (the API's) into a dayjs pattern.
 * Covers the tokens the onboarding catalogue offers: dd, d, MM, MMM, MMMM,
 * yy, yyyy, HH, hh, mm, ss, tt, and quoted literals.
 */
export const toDayjsPattern = (dotNetPattern: string): string =>
  dotNetPattern.replaceAll(
    /'([^']*)'|"([^"]*)"|y+|d+|tt?/g,
    (token, single?: string, double?: string) => {
      if (single !== undefined || double !== undefined)
        return `[${single ?? double}]`;
      if (token.startsWith('y')) return token.length <= 2 ? 'YY' : 'YYYY';
      if (token.startsWith('d')) return token.length === 1 ? 'D' : 'DD';
      return 'A'; // t / tt -> AM/PM
    }
  );

const resolve = (format: TenantDateFormat): ResolvedFormat => ({
  date: toDayjsPattern(format.datePattern),
  dateTime: toDayjsPattern(format.dateTimePattern),
  timeZone: format.timeZone || DEFAULT_TENANT_DATE_FORMAT.timeZone,
});

const readStored = (): TenantDateFormat | null => {
  try {
    const raw = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TenantDateFormat) : null;
  } catch {
    return null;
  }
};

let current: ResolvedFormat = resolve(
  readStored() ?? DEFAULT_TENANT_DATE_FORMAT
);

/** Activates the tenant's format (called once after login). */
export const setTenantDateFormat = (format: TenantDateFormat | null): void => {
  const value = format ?? DEFAULT_TENANT_DATE_FORMAT;
  current = resolve(value);
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode): the format still applies for this page load.
  }
};

/** dayjs pattern for date pickers and date-only text, e.g. "DD/MM/YYYY". */
export const tenantDatePattern = (): string => current.date;

/** dayjs pattern for date + time text, e.g. "DD/MM/YYYY hh:mm A". */
export const tenantDateTimePattern = (): string => current.dateTime;

/** IANA time zone of the tenant, e.g. "Asia/Singapore". */
export const tenantTimeZone = (): string => current.timeZone;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:[\d.]+)?(Z|[+-][\d:]+)?$/;
// "Z", "+08:00" or "+0800" -- not the "-22" that ends a bare "2026-05-22".
const HAS_ZONE = /(Z|[+-]\d{2}:\d{2}|[+-]\d{4})$/;

/** True for ISO-8601 text: "2026-05-22", "2026-05-22T14:35:00Z" ... */
export const isIsoDate = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return ISO_DAY.test(text) || ISO_INSTANT.test(text);
};

/**
 * Reads a date the API returned — ISO-8601 or the tenant's own format — or a
 * value already held as a Date/Dayjs. Returns null for anything else, never a
 * guess.
 */
export const parseApiDate = (value: unknown): Dayjs | null => {
  if (value === null || value === undefined || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : dayjs(value);
  if (typeof value !== 'string') return null;

  const text = value.trim();
  if (isIsoDate(text)) {
    const parsed = dayjs(text);
    return parsed.isValid() ? parsed : null;
  }

  for (const pattern of [current.dateTime, current.date]) {
    const parsed = dayjs(text, pattern, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
};

/**
 * Date-only display text. Text the API already formatted is returned as is;
 * ISO values are shown in the tenant's date pattern without a time-zone shift
 * (a business date stays the date it was stored as).
 */
export const formatDisplayDate = (value: unknown, fallback = '-'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' && !isIsoDate(value)) return value;

  if (typeof value === 'string' && HAS_ZONE.test(value.trim())) {
    const asUtc = dayjs.utc(value.trim());
    return asUtc.isValid() ? asUtc.format(current.date) : fallback;
  }

  const parsed = parseApiDate(value);
  return parsed ? parsed.format(current.date) : fallback;
};

/**
 * Date + time display text. Text the API already formatted is returned as is;
 * an ISO timestamp is UTC (a value without an offset included, as the API
 * stores them) and is shown in the tenant's time zone.
 */
export const formatDisplayDateTime = (
  value: unknown,
  fallback = '-',
  timeZone?: string
): string => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' && !isIsoDate(value)) return value;

  const instant =
    typeof value === 'string'
      ? dayjs.utc(value.trim())
      : (parseApiDate(value)?.utc() ?? null);
  if (!instant?.isValid()) return fallback;

  return instant.tz(timeZone || current.timeZone).format(current.dateTime);
};

/** The calendar date as "YYYY-MM-DD" — the form the API expects in requests. */
export const toApiDate = (value: unknown): string | null =>
  parseApiDate(value)?.format('YYYY-MM-DD') ?? null;

const ISO_IN_TEXT = /\b\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}[\d:.]*Z?)?\b/g;

/**
 * Rewrites ISO dates embedded in free text as tenant dates (date only), e.g.
 * "Share Date - 2020-10-21 to 2026-10-21T23:59:59.9999999" ->
 * "Share Date - 21/10/2020 to 21/10/2026". For text stored before the API
 * formatted it, such as the filter summary of an earlier report run.
 */
export const formatIsoDatesInText = (text: string): string =>
  text.replaceAll(ISO_IN_TEXT, match => {
    // The calendar date as written, never shifted (a "to" bound of 23:59:59 stays that day).
    const day = dayjs(match.slice(0, 10), 'YYYY-MM-DD', true);
    return day.isValid() ? day.format(current.date) : match;
  });
